import React from "react";
import { Task } from "../utils/taskGenerator";

interface Props {
  tasks: Task[];
}

const TimelineView: React.FC<Props> = ({ tasks }) => {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const daysInMonth = endOfMonth.getDate();
  const dayWidth = 40; // px per day

  const priorityColors: Record<string, string> = {
    Critical: "bg-red-600",
    High: "bg-orange-500",
    Medium: "bg-yellow-400",
    Low: "bg-green-500",
  };

  return (
    <div className="overflow-x-auto border rounded bg-white p-2 sm:p-4 relative">
      {/* Timeline header */}
      <div className="flex mb-2">
        {Array.from({ length: daysInMonth }, (_, i) => {
          const date = new Date(
            today.getFullYear(),
            today.getMonth(),
            i + 1
          ).toISOString().split("T")[0];
          return (
            <div
              key={i}
              className="text-[10px] sm:text-xs text-gray-500 border-r"
              style={{ width: dayWidth }}
            >
              {i + 1}
            </div>
          );
        })}
      </div>

      {/* Today marker */}
      <div
        className="absolute top-0 bottom-0 border-l-2 border-blue-600"
        style={{
          left: (today.getDate() - 1) * dayWidth + 60, // offset for padding
        }}
      />

      {/* Empty state */}
      {tasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 text-gray-500 italic">
          <span>No tasks scheduled for this month</span>
          <span className="text-xs mt-1">Try clearing filters or adding tasks</span>
        </div>
      ) : (
        <div className="relative space-y-2">
          {tasks.map((task) => {
            const start = task.startDate
              ? new Date(task.startDate)
              : new Date(task.dueDate);
            const end = new Date(task.dueDate);

            // Clamp to month
            const startDay = Math.max(1, Math.min(daysInMonth, start.getDate()));
            const endDay = Math.max(1, Math.min(daysInMonth, end.getDate()));

            const width = (endDay - startDay + 1) * dayWidth || dayWidth;

            // Label for due date
            const todayISO = today.toISOString().split("T")[0];
            const overdueDays =
              new Date(task.dueDate).getTime() < today.getTime()
                ? Math.floor(
                    (today.getTime() - new Date(task.dueDate).getTime()) /
                      (1000 * 60 * 60 * 24)
                  )
                : 0;

            let dueLabel = task.dueDate;
            if (task.dueDate === todayISO) {
              dueLabel = "Due Today";
            } else if (overdueDays > 7) {
              dueLabel = `${overdueDays} days overdue`;
            }

            return (
              <div
                key={task.id}
                className={`h-6 rounded text-[10px] sm:text-xs text-white flex items-center px-1 sm:px-2 ${priorityColors[task.priority]}`}
                style={{
                  position: "relative",
                  left: (startDay - 1) * dayWidth,
                  width,
                }}
              >
                {task.title} — {dueLabel}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TimelineView;
